import { isDisabledCollateral } from './../../../disabled-collateral/isDisabledCollateral';
import { BigNumber } from '@ethersproject/bignumber';
import { getIsAtRiskOfLiquidation, pick } from '@silo/utils';
import { BasicMarkets } from 'app/entities/BasicMarket';
import { GetConfigurationForSilo } from '@silo/config-legacy';
import { DecoratedSilo, SiloAsset } from 'app/queries/decoratedSilo/types';
import {
  getBaseAsset,
  getDecoratedSiloBorrowingPowerUsed,
  getDecoratedSiloHealthFactor,
  getLoanAssets,
} from 'app/queries/decoratedSilo/utilities';
import { mapValues } from 'lodash';
import { LTV, calculateLTVFromDecoratedSilo } from '../../../../util/ltv';
import { getLiquidationPriceFromLtv } from './Positions';
import { getIsSiloDeprecated } from 'app/features/silo/deprecated/isSiloDeprecated';
import {
  ChecksumAddress,
  DEFAULT_PROTOCOL_KEY,
  getPointsLabel,
  makeAddressSync,
  positionTypes,
  ProtocolInstance,
} from '@silo/types';
import { PointsInfo, PositionAsset, Positions } from '@silo/ui';
import { getConfigAssetPoints, getConfigDepositMode, getConfigIsDebtAprFixed } from '@silo/config';

interface FromFallbackPositionsParams {
  getConfigurationForSilo: GetConfigurationForSilo;
  markets?: BasicMarkets;
  decoratedSilos: DecoratedSilo[];
  protocolInstance: ProtocolInstance;
}

export function fromFallbackPositions({
  getConfigurationForSilo,
  markets,
  decoratedSilos,
  protocolInstance,
}: FromFallbackPositionsParams): Positions[] {
  /**
   * Keep a silo if the user actually holds a position in it, regardless of
   * whether every asset has a working oracle price. This ensures positions stay
   * visible (and withdrawable) when an oracle is removed and the price reads 0.
   */
  const validSilos = decoratedSilos.filter(({ assets }) =>
    assets.some(
      (asset) =>
        asset.collateralBalance.toBigInt() > 0n ||
        asset.collateralOnlyBalance.toBigInt() > 0n ||
        asset.debtBalance.toBigInt() > 0n
    )
  );

  return validSilos.map((decoratedSilo) =>
    fromDecoratedSilo({ getConfigurationForSilo, markets, decoratedSilo, protocolInstance })
  );
}

interface FromDecoratedSiloParams {
  getConfigurationForSilo: GetConfigurationForSilo;
  markets?: BasicMarkets;
  decoratedSilo: DecoratedSilo;
  protocolInstance?: ProtocolInstance;
}

export function fromDecoratedSilo({
  getConfigurationForSilo,
  markets,
  decoratedSilo,
  protocolInstance,
}: FromDecoratedSiloParams): Positions {
  const { siloAddress, userLTV, userLiquidationThreshold } = decoratedSilo;
  const { symbol } = getBaseAsset(decoratedSilo);
  const ltv = calculateLTVFromDecoratedSilo(decoratedSilo);
  const loanAssets = getLoanAssets(decoratedSilo);
  const healthFactor = getDecoratedSiloHealthFactor(decoratedSilo);
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  return {
    siloAddress,
    siloSymbol: symbol,
    isSiloDeprecated: getIsSiloDeprecated(
      protocolKey,
      siloAddress,
      markets
        ? {
            ids: markets.map((market) => market.id),
            getIsMarketArchived: (id) => markets?.find((market) => market.id === id)?.archived ?? false,
          }
        : undefined
    ).isDeprecated,
    userLTV: userLTV.toBigInt(),
    userLiquidationThreshold: userLiquidationThreshold.toBigInt(),
    healthFactor,
    borrowingPowerUsed: getDecoratedSiloBorrowingPowerUsed(decoratedSilo),
    assets: decoratedSilo.assets.map((siloAsset) => fromSiloAsset({ ltv, protocolInstance, siloAsset, siloAddress })),
    isAtRiskOfLiquidation: getIsAtRiskOfLiquidation(
      healthFactor,
      loanAssets.map((asset) => asset.liquidationThreshold.toBigInt())
    ),
  };
}

interface FromSiloAssetParams {
  protocolInstance?: ProtocolInstance;
  siloAddress: ChecksumAddress;
  ltv: LTV | null;
  siloAsset: SiloAsset;
}

function fromSiloAsset({ protocolInstance, siloAddress, ltv, siloAsset }: FromSiloAssetParams): PositionAsset {
  const { depositAPY, depositRewardsApr, lendingMerklRewards, borrowAPY, borrowRewardsApr, assetAddress, assetKey } =
    siloAsset;

  const liquidationPrice = getLiquidationPriceFromLtv(ltv, assetAddress);

  const marketAddress = makeAddressSync(siloAddress);
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;
  const isDebtAprFixed = getConfigIsDebtAprFixed({ protocolKey, marketAddress, assetKey });
  const configAssetPoints = getConfigAssetPoints({ protocolKey, marketAddress, assetKey });
  const isProtectedOnly = getConfigDepositMode({ protocolKey, marketAddress, assetKey }) === 'protectedOnly';

  const points: PointsInfo[] = [];

  for (const type of positionTypes) {
    const typePoints = configAssetPoints[type];

    if (typePoints) {
      const pointsInfoArray: PointsInfo[] = typePoints.map((pointsItem) => {
        return { type, label: getPointsLabel(pointsItem) };
      });

      points.push(...pointsInfoArray);
    }
  }

  return {
    liquidationPrice,
    merklRewards: lendingMerklRewards,
    isBorrowAprFixed: isDebtAprFixed,
    isProtectedOnly,
    depositApr: depositAPY.toBigInt(),
    depositRewardsApr: depositRewardsApr.toBigInt(),
    borrowApr: borrowAPY.toBigInt(),
    borrowRewardsApr: borrowRewardsApr.toBigInt(),
    priceInUSD: null,
    isDisabledCollateral: isDisabledCollateral(siloAsset.maximumLTV.toBigInt()),
    ...pick(siloAsset, ['assetAddress', 'symbol', 'decimals']),
    ...mapValues(
      pick(siloAsset, ['collateralBalance', 'collateralOnlyBalance', 'debtBalance', 'maximumLTV']),
      toBigInt
    ),
    points,
  };
}

const toBigInt = (bigNumber: BigNumber) => bigNumber.toBigInt();
